import axios from 'axios';
import { API_BASE_URL, buildApiUrl } from '../../config/env';
import {
  BusinessBranchUpsertRequest,
  BusinessProfile,
  BusinessProfileUpsertRequest,
  CustomerAuthResponse,
  CustomerSignUpRequest,
  CustomerUser,
} from '../../types/customerAuth';
import { sanitizeApiErrorMessage } from './errorMessages';
import { clearCustomerAuthFlow } from '../storage/customerFlow';
import { getWebSessionContext } from '../storage/authTokens';
import { attachRoleAuthInterceptors } from './authInterceptors';
import { logoutRoleSession, normalizeTokenBundle, refreshRoleSession } from './tokenRefresh';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

const openEndpoints = [
  '/auth-service/v1/auth/create',
  '/auth-service/v1/auth/verify-email',
  '/auth-service/v1/auth/resend-email-otp',
  '/auth-service/oauth2',
];

export function customerEndpointNeedsAuth(requestUrl: string): boolean {
  return !openEndpoints.some((endpoint) => requestUrl.startsWith(endpoint));
}

attachRoleAuthInterceptors(api, {
  role: 'customer',
  endpointNeedsAuth: customerEndpointNeedsAuth,
  clearSession: clearCustomerAuthFlow,
});

export function normalizeCustomerApiErrorMessage(rawMessage: string, fallbackMessage: string): string {
  const message = rawMessage.trim();

  if (!message) {
    return fallbackMessage;
  }

  if (/email is already in use!?/i.test(message)) {
    return 'This email is already registered. Try logging in instead.';
  }

  if (
    /users_phone_key/i.test(message) ||
    /duplicate key value violates unique constraint.*phone/i.test(message) ||
    /phone.*already (exists|in use|registered)/i.test(message)
  ) {
    return 'This phone number is already linked to another account.';
  }

  if (/network error/i.test(message)) {
    return 'We could not reach the ShiftWagon server right now. Please try again.';
  }

  return sanitizeApiErrorMessage(message, fallbackMessage);
}

export function mapCustomerApiStatusMessage(status?: number): string | null {
  if (status === 409) {
    return 'This email is already registered. Try logging in instead.';
  }

  return null;
}

function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data;
    const mappedStatusMessage = mapCustomerApiStatusMessage(status);

    if (mappedStatusMessage) {
      return mappedStatusMessage;
    }

    if (typeof responseData === 'string' && responseData.trim()) {
      return normalizeCustomerApiErrorMessage(responseData, fallbackMessage);
    }

    if (responseData && typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      const candidate =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.details === 'string' && data.details);

      if (candidate) {
        return normalizeCustomerApiErrorMessage(candidate, fallbackMessage);
      }
    }

    if (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || '')) {
      return 'We could not reach the ShiftWagon server right now. Please try again.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return normalizeCustomerApiErrorMessage(error.message, fallbackMessage);
  }

  return fallbackMessage;
}

function mapUser(user: any): CustomerUser {
  return {
    id: String(user.id),
    name: user.name,
    username: user.username,
    email: user.email,
    roles: user.roles ?? [],
    phone: user.phone,
    phoneVerified: user.phoneVerified,
    emailVerified: user.emailVerified,
    googleLinked: user.googleLinked,
    onboardingsStep: user.onboardingsStep,
    attachments: user.attachments ?? {},
    accountType: user.accountType === 'BUSINESS' ? 'BUSINESS' : 'INDIVIDUAL',
    businessProfileId: user.businessProfileId ?? null,
    businessVerificationStatus: user.businessVerificationStatus ?? null,
  };
}

function mapBusinessProfile(response: any): BusinessProfile {
  return {
    id: String(response.id),
    ownerUserId: String(response.ownerUserId),
    ownerName: response.ownerName ?? null,
    ownerEmail: response.ownerEmail ?? null,
    ownerPhone: response.ownerPhone ?? null,
    legalName: response.legalName ?? '',
    tradingName: response.tradingName ?? null,
    registrationNumber: response.registrationNumber ?? null,
    vatNumber: response.vatNumber ?? null,
    businessEmail: response.businessEmail ?? null,
    businessPhone: response.businessPhone ?? null,
    headOfficeAddress: response.headOfficeAddress ?? null,
    representativeName: response.representativeName ?? null,
    representativeEmail: response.representativeEmail ?? null,
    verificationStatus: response.verificationStatus ?? 'DRAFT',
    verificationNote: response.verificationNote ?? null,
    submittedAt: response.submittedAt ?? null,
    reviewedAt: response.reviewedAt ?? null,
    createdDate: response.createdDate ?? null,
    updatedDate: response.updatedDate ?? null,
    branches: Array.isArray(response.branches)
      ? response.branches.map((branch: any) => ({
          id: String(branch.id),
          branchName: branch.branchName ?? '',
          address: branch.address ?? '',
          latitude: branch.latitude ?? null,
          longitude: branch.longitude ?? null,
          contactEmail: branch.contactEmail ?? null,
          contactPhone: branch.contactPhone ?? null,
          defaultBranch: Boolean(branch.defaultBranch),
        }))
      : [],
  };
}

function mapAuthResponse(response: any): CustomerAuthResponse {
  const tokenBundle = normalizeTokenBundle(response);
  return {
    success: Boolean(response.success),
    message: response.message ?? '',
    token: tokenBundle.accessToken ?? undefined,
    accessToken: tokenBundle.accessToken,
    refreshToken: tokenBundle.refreshToken,
    accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
    refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
    userResponse: response.userResponse ? mapUser(response.userResponse) : undefined,
    otpRequired: Boolean(response.otpRequired),
    otpChannel: response.otpChannel ?? null,
    maskedDestination: response.maskedDestination ?? null,
    challengeId: response.challengeId ?? null,
    challengeExpiresAt: response.challengeExpiresAt ?? null,
    otpExpiresAt: response.otpExpiresAt ?? null,
  };
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

export function getGoogleSignUpUrl(): string {
  return buildApiUrl('auth-service/v1/auth/oauth2/authorize/google?client=web');
}

export async function customerSignUp(
  payload: CustomerSignUpRequest,
): Promise<CustomerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/create', payload);
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not create account.'));
  }
}

export async function customerSignIn(payload: {
  email: string;
  password: string;
}): Promise<CustomerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/login', {
      ...payload,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;

      if (responseData && typeof responseData === 'object') {
        const data = responseData as Record<string, unknown>;
        const candidateMessage =
          (typeof data.message === 'string' && data.message) ||
          (typeof data.error === 'string' && data.error) ||
          (typeof data.details === 'string' && data.details) ||
          '';
        const otpRequired =
          Boolean(data.otpRequired) ||
          Boolean(data.challengeId) ||
          /otp verification required/i.test(candidateMessage);

        if (otpRequired) {
          return mapAuthResponse({
            ...data,
            success: Boolean(data.success),
            message: candidateMessage || 'OTP verification required for admin login.',
            otpRequired: true,
            maskedDestination:
              typeof data.maskedDestination === 'string' ? data.maskedDestination : null,
            challengeId: typeof data.challengeId === 'string' ? data.challengeId : null,
            challengeExpiresAt:
              typeof data.challengeExpiresAt === 'string' ? data.challengeExpiresAt : null,
            otpExpiresAt: typeof data.otpExpiresAt === 'string' ? data.otpExpiresAt : null,
          });
        }
      }
    }

    throw new Error(extractErrorMessage(error, 'Could not sign in.'));
  }
}

export async function requestCustomerPasswordReset(email: string): Promise<{
  challengeId: string;
  message: string;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/forgot-password', { email });
    return {
      challengeId: String(response.data?.challengeId ?? ''),
      message: response.data?.message ?? 'If the account exists, a password reset code has been sent.',
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to send password reset code. Please try again.'));
  }
}

export async function resetCustomerPassword(payload: {
  challengeId: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  try {
    await publicApi.post('/auth-service/v1/auth/reset-password', payload);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to reset password. Please check your code and try again.'));
  }
}

export async function verifyCustomerEmail(email: string, code: string): Promise<CustomerAuthResponse> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/verify-email', {
      email,
      code,
      ...getWebSessionContext(),
    });
    return mapAuthResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Email verification failed.'));
  }
}

export async function refreshCustomerAuthSession(): Promise<string | null> {
  return refreshRoleSession('customer');
}

export async function logoutCustomerAuthSession(): Promise<void> {
  await logoutRoleSession('customer');
  clearCustomerAuthFlow();
}

export async function resendCustomerEmailOtp(email: string): Promise<void> {
  try {
    await publicApi.post('/auth-service/v1/auth/resend-email-otp', { email });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the email code.'));
  }
}

export async function fetchCustomerById(userId: string, token: string): Promise<CustomerUser> {
  try {
    const response = await api.get(`/user-management/api/users/${encodeURIComponent(userId)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return mapUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load customer details.'));
  }
}

export async function uploadCustomerFaceLiveness(
  userId: string,
  challengeType: 'STRAIGHT' | 'LEFT' | 'RIGHT',
  sequenceOrder: 1 | 2 | 3,
  file: Blob,
): Promise<CustomerUser> {
  const formData = new FormData();
  formData.append('file', file, `face_liveness_${challengeType.toLowerCase()}_${Date.now()}.jpg`);

  try {
    const response = await api.post(
      `/user-management/api/users/onboarding/files/face-liveness?driverId=${encodeURIComponent(
        userId,
      )}&challengeType=${challengeType}&sequenceOrder=${sequenceOrder}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      },
    );

    return mapUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload face verification images.'));
  }
}

export async function sendCustomerPhoneOtp(userId: string, phoneE164: string): Promise<void> {
  try {
    await api.post('/user-management/api/driver-auth/phone/send-otp-v2', {
      userId,
      phoneE164,
    });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the phone verification code.'));
  }
}

export async function verifyCustomerPhoneOtp(
  userId: string,
  phoneE164: string,
  code: string,
): Promise<CustomerUser> {
  try {
    const response = await api.post('/user-management/api/driver-auth/phone/verify-otp-v2', {
      userId,
      phoneE164,
      code,
    });

    return mapUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not verify your phone number.'));
  }
}

export async function fetchMyBusinessOnboarding(): Promise<BusinessProfile> {
  try {
    const response = await api.get('/user-management/api/business-onboarding/me');
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load business onboarding.'));
  }
}

export async function upsertBusinessProfile(payload: BusinessProfileUpsertRequest): Promise<BusinessProfile> {
  try {
    const response = await api.put('/user-management/api/business-onboarding/profile', payload);
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save business profile.'));
  }
}

export async function addBusinessBranch(payload: BusinessBranchUpsertRequest): Promise<BusinessProfile> {
  try {
    const response = await api.post('/user-management/api/business-onboarding/branches', payload);
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save business branch.'));
  }
}

export async function updateBusinessBranch(branchId: string, payload: BusinessBranchUpsertRequest): Promise<BusinessProfile> {
  try {
    const response = await api.put(
      `/user-management/api/business-onboarding/branches/${encodeURIComponent(branchId)}`,
      payload,
    );
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update business branch.'));
  }
}

export async function deleteBusinessBranch(branchId: string): Promise<BusinessProfile> {
  try {
    const response = await api.delete(
      `/user-management/api/business-onboarding/branches/${encodeURIComponent(branchId)}`,
    );
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not delete business branch.'));
  }
}

export async function submitBusinessOnboarding(): Promise<BusinessProfile> {
  try {
    const response = await api.post('/user-management/api/business-onboarding/submit');
    return mapBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not submit business onboarding.'));
  }
}
